import { Injectable } from '@angular/core';

import { IParty, IPartyNotification } from '@tablesready/common-ui';

import { PartiesService } from '@tablesready/frontend-api-services';
import { ApiClientService } from '@tablesready/api-client';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';

@Injectable({
  providedIn: 'root',
})
export class MessageSenderService {
  constructor(
    private partiesService: PartiesService,
    private alertsService: AlertsService,
    private http: ApiClientService
  ) {}

  private smsUrl = '/v.1/smsPage';
  private voiceUrl = '/v.1/voicePage';

  formatNotification(
    party: IParty,
    message: string,
    type: string,
    label = ''
  ) {
    const notification : IPartyNotification = {
      party: party._id,
      phone: party.phone,
      type,
      label: label,
      time: new Date().getTime(),
      status_sent: 'inProcess',
    };
    if (label === 'chat') {
      notification.message = message;
    }
    return notification;
  }

  failMessage(notification: IPartyNotification, party: IParty) {
    party.notifications.forEach((n, idx) => {
      if (n.party === notification.party && n.time === notification.time) {
        party.notifications[idx].status_sent = 'not_sent';
      }
    });
    this.partiesService.updateParty(party);
  }

  smsPage(party: IParty, msg: string, label: string) {
    party.notifications = party.notifications || [];

    const notification = this.formatNotification(party, msg, 'sms', label);
    party.notifications.push(notification);
    this.partiesService.updateParty(party);

    this.http.post<any>(this.smsUrl, {notification})
      .subscribe(
        ({error, success}) => {
          if (!success) {
            this.failMessage(notification, party);
            if (error && error === 'unverified') {
              this.alertsService.add({
                heading: 'Error:',
                type: IAlertTypes.warning,
                body: 'Please verify your email in order to send notifications.',
              });
            } else if (error && error === 'expired') {
              this.alertsService.add({
                heading: 'Error:',
                type: IAlertTypes.warning,
                body: 'Your subscription is expired. Please log out and back in if this is incorrect.',
              });
            } else if (error) {
              // Any other failure on a user-initiated send (ready, chat, next,
              // to-go ready, etc.) — surface it. The backend now returns
              // success:false for anticipated delivery rejections (landline,
              // invalid/unsubscribed number) instead of an HTTP error, so this
              // branch is what makes those visible regardless of label.
              this.alertsService.add({
                heading: 'Text message failure:',
                type: IAlertTypes.warning,
                body: error,
              });
            }
          }
        },
        (error) => {
          this.failMessage(notification, party);
          const errorMessage =
            error && error.data && error.data.error
              ? error.data.error
              : 'server error, please try again';

          this.alertsService.add({
            heading: 'Text message failure:',
            type: IAlertTypes.warning,
            body: errorMessage,
          });
        }
      );
  }

  voicePage(party: IParty, label: string) {
    party.notifications = party.notifications || [];

    const notification = this.formatNotification(party, '', 'voice', label);

    party.notifications.push(notification);

    this.partiesService.updateParty(party);

    this.http
      .post(this.voiceUrl, {
        notification,
      })
      .subscribe(
        (resp: any) => {
          if (!resp.success) {
            this.failMessage(notification, party);
            if (resp.error && resp.error === 'unverified') {
              this.alertsService.add({
                heading: 'Error:',
                type: IAlertTypes.warning,
                body: 'Please verify your email in order to send notifications.',
              });
            } else {
              this.alertsService.add({
                heading: 'Voice call error:',
                type: IAlertTypes.warning,
                body: resp.notification.error || resp.error,
              });
            }
          }
        },
        (error) => {
          this.alertsService.add({
            heading: 'Voice call error:',
            type: IAlertTypes.warning,
            body: error.error || error,
          });

          party.notifications.forEach((n) => {
            if (
              n.party !== error.notification.party ||
              n.time !== error.notification.time
            ) {
              return;
            }
            n.status_sent = 'not_sent';
            this.partiesService.updateParty(party);
          });
        }
      );
  }
}
